import log from 'loglevel';
import { API_BASE_URL } from '@/app.configs';
import { ITokenStrategy } from '@/interfaces/ITokenStrategy';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { useDeviceParams } from '@/composables/device.config';

// Event bus for API errors
export const apiEvents = {
	onError: (error: any) => {
		// This will be replaced with actual implementation during initialization
		log.warn('API error handler not initialized', error);
	},
	onInvalidToken: () => {
		// This will be replaced with actual implementation during initialization
		log.warn('Invalid token handler not initialized');
	}
};

export class ApiService {

	private axiosInstance: AxiosInstance;
	private tokenStrategy: ITokenStrategy;
	private deviceParams = useDeviceParams();

	constructor(tokenStrategy: ITokenStrategy) {
		this.axiosInstance = axios.create({
			baseURL: API_BASE_URL,
			timeout: 20000,
		});

		this.axiosInstance.interceptors.request.use(async (config) => {
			config.params = config.params || {};
			config.params.fromBrowser = true;
			
			// Wait for device params to be initialized if they're not ready
			if (!this.deviceParams.deviceParams.value) {
				await this.deviceParams.reloadDeviceParams();
			}
			
			config.params.web_device = JSON.stringify(this.deviceParams.deviceParams.value);

			return config;
		}, (error) => {
			return Promise.reject(error);
		});

		this.tokenStrategy = tokenStrategy;
		this.initializeResponseInterceptor();
	}

	async get(endpoint: string, params?: any): Promise<any> {
		try {
			const modifiedParams = this.tokenStrategy.appendTokenToParams(params);
			const url = this.createUrl(endpoint);

			const response = await this.axiosInstance.get(url, { params: modifiedParams });
			const { meta, ...filteredData } = response.data;
			return filteredData;
		} catch (error) {
			// Check if this is an authentication error
			if (error instanceof Error && error.message.includes('User is not authenticated')) {
				log.info(`API call to ${endpoint} prevented: User not authenticated`);
				return { authError: true };
			}
			
			// Let the caller handle other errors
			this.logError('GET', endpoint, error);
			throw error;
		}
	}

	async post(endpoint: string, data?: any, params?: any): Promise<any> {
		try {
			const token = this.tokenStrategy.appendTokenToParams();
			const url = this.createUrl(endpoint);

			const defaultConfig: AxiosRequestConfig = {
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			};
			const updatedConfig = { ...defaultConfig, params: { ...params, ...token } };

			const response = await this.axiosInstance.post(url, data, updatedConfig);
			const { meta, ...filteredData } = response.data;
			return filteredData;
		} catch (error: any) {
			// Check if this is an authentication error
			if (error instanceof Error && error.message.includes('User is not authenticated')) {
				log.info(`API call to ${endpoint} prevented: User not authenticated`);
				return { authError: true };
			}
			
			// Let the caller handle other errors
			this.logError('POST', endpoint, error);
			throw error;
		}
	}

	async put(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<any> {
		try {
			const modifiedParams = this.tokenStrategy.appendTokenToParams(data);
			const url = this.createUrl(endpoint);
			
			const response = await this.axiosInstance.put(url, modifiedParams, config);
			const { meta, ...filteredData } = response.data;
			return filteredData;
		} catch (error) {
			// Check if this is an authentication error
			if (error instanceof Error && error.message.includes('User is not authenticated')) {
				log.info(`API call to ${endpoint} prevented: User not authenticated`);
				return { authError: true };
			}
			
			// Let the caller handle other errors
			this.logError('PUT', endpoint, error);
			throw error;
		}
	}

	openWindow(endpoint: string, params?: any) {
		const modifiedParams = this.tokenStrategy.appendTokenToParams(params);
		const url = this.createUrl(endpoint);
		const urlParams = new URLSearchParams(modifiedParams).toString();
		window.open(`${url}?${urlParams}`, '_blank');
	}

	private initializeResponseInterceptor() {
		this.axiosInstance.interceptors.response.use(
			this.handleResponse,
			this.handleError
		);
	}

	private handleResponse(response: any) {
		return response;
	}

	private handleError = async (error: any) => {
		const originalRequest = error.config;
		
		// Check for invalid auth token error
		if (error.response && 
			error.response.status === 400 && 
			error.response.data && 
			error.response.data.error && 
			(error.response.data.error.type === 'InvalidAuthTokenException' || 
			 error.response.data.error.message === 'Invalid Auth Token: Token has been tampered with!')) {
			
			log.warn('Invalid auth token detected, logging out user');
			
			// Trigger the invalid token event to log out the user
			apiEvents.onInvalidToken();
			
			// Don't retry the request in this case
			return Promise.reject(error);
		}
		
		if (error.code === 'ECONNABORTED' || error.message === 'net::ERR_NETWORK_CHANGED') {
			// Check if this request has already been retried
			if (!originalRequest._retry) {
				originalRequest._retry = true;
				// Wait a moment and then retry the request
				await this.delay(1000);  // Wait for 1 second before retrying
				return this.axiosInstance(originalRequest);
			}
		}
		
		if (error.code === 'ECONNABORTED') { // Added handling for timeout specific error
            log.error(`API request timed out: ${error.message}`);
        } else {
            log.error(`API request failed: ${error.message}`); // If the request has already been retried or the error is different, throw it
        }
		throw error;
	};

	private delay(ms: number) {
		return new Promise(resolve => setTimeout(resolve, ms));
	}

	private createUrl(endpoint: string): string {
		return `${this.axiosInstance.defaults.baseURL}${endpoint}`;
	}

	private logError(method: string, endpoint: string, error: any) {
		log.error(`${method} request to ${endpoint} failed:`, error);
		// Notify global error handler
		apiEvents.onError(error);
	}
}